import KYDCheckbox from "src/ui/KYDCheckbox";
import { SearchIcon } from "@chakra-ui/icons";
import KYDInput from "src/ui/KYDInput";
import KYDTimePicker from "src/ui/KYDTimePicker";
import { HStack, VStack, Image, Text, Box } from "@chakra-ui/react";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { KYDAdminEventSummary } from "@common/interfaces/KYDEvent";
import {
  useInfiniteHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { SearchClient } from "algoliasearch";
import KYDImageUpload from "src/ui/KYDImageUpload";
import {
  BodyDoc,
  BodyNode,
  KYDEventBlockData,
  KYDEventBlockDataV2,
} from "@common/interfaces/KYDCampaign";
import { Controller, useFormContext } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { KYDAdminFeed } from "@common/interfaces/KYDOrganization";
import { S3Response } from "react-s3-uploader";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import KYDTextArea from "src/ui/KYDTextArea";
import { TipTapEditor } from "src/ui/editor";
import { Editor } from "@tiptap/react";
import KYDDataButtonSection from "src/ui/KYDDataButtonSection";

dayjs.extend(utc);
dayjs.extend(timezone);

const EventBlock = ({
  externalEvents,
  substitutionsItems,
  // eventData,
  blockIndex,
  eventIndex,
  createUploadUrl,
  type,
  isDisabled,
  timezone,
}: {
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: (params: S3Response) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  externalEvents: KYDAdminFeed["external_events"];
  substitutionsItems: {
    key: string;
    label: string;
    value: string;
  }[];
  eventData: KYDEventBlockData | KYDEventBlockDataV2 | null;
  blockIndex: number;
  eventIndex: number;
  type: "event1col" | "event2col" | "eventList";
  isDisabled?: boolean;
  timezone?: string;
}) => {
  const { items: events } = useInfiniteHits<KYDAdminEventSummary>({
    escapeHTML: false,
    showPrevious: false,
  });
  const { status: searchStatus } = useInstantSearch();
  const { refine } = useSearchBox();
  const [searchQuery, setSearchQuery] = useState("");
  const { control, setValue, watch, trigger } = useFormContext();
  const filteredExternalEvents = externalEvents
    .filter((event) =>
      event.title?.toLowerCase().includes(searchQuery?.toLowerCase())
    )
    .map((event) => ({
      ...event,
      name: event.title,
    }));
  const key = type === "event1col" ? "event" : `events.${eventIndex}`;
  const editorRef = useRef<Editor>(null);

  const handleImageUpload = (result: {
    cdn_url: string;
    temp_url: string;
    link?: string;
  }) => {
    setValue(`blocks.${blockIndex}.${key}.custom_image`, result.cdn_url, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleSelect = (
    item: KYDAdminEventSummary | KYDAdminFeed["external_events"][number]
  ) => {
    const event = {
      id: item.id,
      name: "name" in item ? item.name : item.title,
      image: item.image,
      start_at: item.start_at,
      end_at: "end_at" in item ? item.end_at : "",
      link: item.link,
      venue: "venues" in item ? item.venue.name : item.venue_name,
      is_external: "name" in item ? false : true,
    } as KYDEventBlockData["event"];

    setValue(`blocks.${blockIndex}.${key}.event`, event);
    setValue(`blocks.${blockIndex}.${key}.show_time`, null);
    setValue(`blocks.${blockIndex}.${key}.door_time`, null);
    setValue(`blocks.${blockIndex}.${key}.custom_image`, null);

    trigger(`blocks`);

    if ("name" in item) {
      setSearchQuery(item.name);
    } else {
      setSearchQuery(item.title);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    refine(query);
  };

  const doorTime = watch(`blocks.${blockIndex}.${key}.door_time`, null);
  const showTime = watch(`blocks.${blockIndex}.${key}.show_time`, null);
  const event = watch(`blocks.${blockIndex}.${key}.event`);
  const body = watch(`blocks.${blockIndex}.${key}.body`);
  const customImage = watch(`blocks.${blockIndex}.${key}.custom_image`);
  const image = watch(`blocks.${blockIndex}.${key}.event.image`);
  const templateVersion = watch("template_version");

  const handleAddData = (key: string) => {
    const toAdd = substitutionsItems.find((item) => item.key === key);

    editorRef.current?.commands.insertContent(toAdd?.value || "");
  };

  useEffect(() => {
    if (event?.name) {
      setSearchQuery(event.name);
    }
  }, [event?.name]);

  return (
    <VStack gap={3} w="100%">
      <KYDSearchInput
        leftIcon={<SearchIcon color="#969696" fontSize="12px" mt={1} />}
        placeholder="Search for events"
        items={[...events, ...filteredExternalEvents]}
        maxHeight="300px"
        width="100%"
        searchField="name"
        value={searchQuery}
        onChange={handleSearch}
        loading={searchStatus === "loading"}
        onSelect={handleSelect}
        isDisabled={isDisabled}
        itemComponent={(item: KYDAdminEventSummary, index: number) => {
          return (
            <HStack
              key={`${item.id}-${index}`}
              py={2}
              px={3}
              borderBottom={
                index === events.length - 1 ? "none" : "1px solid #E2E8F0"
              }
              w="100%"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Image
                borderRadius="8px"
                width="70px"
                height="70px"
                objectFit="cover"
                src={item.image}
                alt="Event 2 Column"
              />
              <VStack
                w="100%"
                alignItems="flex-start"
                gap={1}
                justifyContent="flex-start"
              >
                <Text fontSize="14px" fontFamily="Helvetica">
                  {item.name}
                </Text>
                <Text fontSize="12px" color="#1A202CB2">
                  {item.display_start_at || "Date"}
                </Text>
              </VStack>
            </HStack>
          );
        }}
      />

      {event && (
        <>
          <KYDImageUpload
            image={customImage || image}
            onChange={handleImageUpload}
            createUploadUrl={createUploadUrl}
            isDisabled={!event?.id || isDisabled}
          />
          <Controller
            name={`blocks.${blockIndex}.${key}.event.link`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                defaultValue={field.value}
                placeholder="Add link to redirect when event is clicked"
                label="Link"
              />
            )}
          />
          <Controller
            name={`blocks.${blockIndex}.${key}.button_text`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                defaultValue={field.value}
                placeholder="Optional"
                label="Button Text"
                w="100%"
              />
            )}
          />
          {templateVersion === 4 ? (
            <Box
              minH={"400px"}
              borderWidth={1}
              borderColor={"gray.300"}
              borderRadius={"md"}
              w="full"
            >
              <TipTapEditor
                showBlockSelector={false}
                ref={editorRef}
                leftContent={
                  <KYDDataButtonSection
                    substitutions={substitutionsItems}
                    onAddData={handleAddData}
                    isDisabled={false}
                  />
                }
                value={body as BodyDoc}
                isDisabled={isDisabled}
                onChange={(value) => {
                  setValue(`blocks.${blockIndex}.${key}.body`, value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </Box>
          ) : (
            <KYDTextArea
              value={body}
              isDisabled={isDisabled}
              label="Body Text"
              onChange={(value) => {
                setValue(
                  `blocks.${blockIndex}.${key}.body`,
                  value.target.value,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                  }
                );
              }}
            />
          )}
          {/* <Text>{event?.body}</Text> */}
          {(doorTime || showTime) && (
            <HStack justifyContent="space-between" w="100%">
              {!!doorTime && (
                <KYDTimePicker
                  value={doorTime ? dayjs(doorTime).tz(timezone) : null}
                  timezone={timezone}
                  onChange={(value) => {
                    if (!event?.start_at) {
                      return;
                    }

                    setValue(
                      `blocks.${blockIndex}.${key}.door_time`,
                      value?.toISOString(),
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                      }
                    );
                  }}
                  isDisabled={isDisabled}
                  label="Doors"
                />
              )}
              {showTime && (
                <KYDTimePicker
                  value={showTime ? dayjs(showTime).tz(timezone) : null}
                  timezone={timezone}
                  onChange={(value) => {
                    if (!event?.start_at) {
                      return;
                    }

                    setValue(
                      `blocks.${blockIndex}.${key}.show_time`,
                      value?.toISOString(),
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                      }
                    );
                  }}
                  isDisabled={isDisabled}
                  label="Show"
                />
              )}
            </HStack>
          )}
          <HStack gap={6} mt={3} w="100%" justifyContent="flex-start">
            <KYDCheckbox
              isChecked={!!doorTime}
              defaultChecked={!!doorTime}
              onChange={(e) => {
                if (!event?.start_at) {
                  return;
                }

                setValue(
                  `blocks.${blockIndex}.${key}.door_time`,
                  e.target.checked ? dayjs(event.start_at).toISOString() : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                  }
                );
              }}
              label="Doors"
              isDisabled={isDisabled || !event?.id}
            />
            <KYDCheckbox
              isChecked={!!showTime}
              defaultChecked={!!showTime}
              onChange={(e) => {
                if (!event?.start_at) {
                  return;
                }

                setValue(
                  `blocks.${blockIndex}.${key}.show_time`,
                  e.target.checked ? dayjs(event.start_at).toISOString() : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                  }
                );
              }}
              label="Showtime"
              isDisabled={isDisabled || !event?.id}
            />
          </HStack>
        </>
      )}
    </VStack>
  );
};

export default EventBlock;
